import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Eye,
  EyeOff,
  Image as ImageIcon,
  Loader2,
  Plus,
  Save,
  X,
} from "lucide-react";
import React, { FC, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import {
  Dropzone,
  DropzoneContent,
  DropzoneEmptyState,
} from "../../components/ui/Dropzone";
import { Input } from "../../components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/Select";
import { fetchWithAuth } from "../../hooks/useAuth";
import { useCloudflareUpload } from "../../hooks/useCloudflareUpload";

interface Category {
  id: number;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
}

interface FormData {
  name: string;
  description: string;
  price: string;
  stock: string;
  hide: boolean;
  images: string[];
  imageUrl: string;
  pages: string;
  categoryId: string;
  cod: string;
  awbCode: string;
  isbn: string;
}

export const AddProductPannel: FC<{
  onSuccess?: () => void;
  onCancel?: () => void;
}> = ({ onSuccess, onCancel }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = React.useState<FormData>({
    name: "",
    description: "",
    price: "",
    stock: "",
    hide: false,
    images: [] as string[],
    imageUrl: "",
    pages: "",
    categoryId: "",
    cod: "",
    awbCode: "",
    isbn: "",
  });

  const [showCoverDropzone, setShowCoverDropzone] = useState(true);
  const [showGalleryDropzone, setShowGalleryDropzone] = useState(false);

  // COVER uploader (single)
  const coverUpload = useCloudflareUpload({
    maxFiles: 1,
    allowedMimeTypes: ["image/*"],
  });

  // GALLERY uploader (multiple)
  const galleryUpload = useCloudflareUpload({
    maxFiles: 10,
    allowedMimeTypes: ["image/*"],
  });

  // Fetch categories
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      try {
        const response = await fetch("/api/carti/categorii");
        if (!response.ok) {
          throw new Error("Eroare la încărcarea categoriilor");
        }
        const result = await response.json();
        console.log("Categories API response (AddProductPannel):", result);

        // Handle the actual API response structure: { status, mesaj, date }
        if (result && result.date && Array.isArray(result.date)) {
          return result.date as Category[];
        }

        // Fallback for different response structures
        if (result && result.data && Array.isArray(result.data)) {
          return result.data as Category[];
        }

        // If data structure is different, try to handle it
        if (Array.isArray(result)) {
          return result as Category[];
        }

        // If no data, return empty array
        return [] as Category[];
      } catch (error) {
        console.error("Categories fetch error:", error);
        // Return empty array on error to prevent undefined
        return [] as Category[];
      }
    },
  });

  // Create product mutation
  const createProduct = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await fetchWithAuth("/api/admin/products", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.name.trim(),
          description: data.description,
          price: Number(data.price),
          stock: Number(data.stock),
          hide: data.hide,
          images: data.images,
          imageUrl: data.imageUrl || undefined,
          pages: data.pages ? Number(data.pages) : undefined,
          categoryId: data.categoryId ? Number(data.categoryId) : undefined,
          cod: data.cod ? Number(data.cod) : undefined,
          awbCode: data.awbCode ? data.awbCode.trim() : undefined,
          isbn: data.isbn ? data.isbn.trim() : undefined,
        }),
      });
      if (!response.ok) throw new Error("Eroare la crearea produsului");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Produs creat cu succes");
      setFormData({
        name: "",
        description: "",
        price: "",
        stock: "",
        hide: false,
        images: [] as string[],
        imageUrl: "",
        pages: "",
        categoryId: "",
        cod: "",
        awbCode: "",
        isbn: "",
      });
      // Reset upload states
      coverUpload.setFiles([]);
      coverUpload.setErrors([]);
      galleryUpload.setFiles([]);
      galleryUpload.setErrors([]);
      setShowCoverDropzone(true);
      setShowGalleryDropzone(false);

      if (onSuccess) onSuccess();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Handle cover image upload results
  useEffect(() => {
    if (coverUpload.isSuccess && coverUpload.uploadedResults.length > 0) {
      const coverUrl = coverUpload.uploadedResults[0].publicUrl;
      setFormData((prev) => ({ ...prev, imageUrl: coverUrl }));
      setShowCoverDropzone(false);
    }
  }, [coverUpload.isSuccess, coverUpload.uploadedResults]);

  // Handle gallery images upload results
  useEffect(() => {
    if (galleryUpload.isSuccess && galleryUpload.uploadedResults.length > 0) {
      const newUrls = galleryUpload.uploadedResults.map((r) => r.publicUrl);
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...newUrls],
      }));
      setShowGalleryDropzone(false);
    }
  }, [galleryUpload.isSuccess, galleryUpload.uploadedResults]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createProduct.mutate(formData);
  };

  return (
    <Card className="bg-white shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Adaugă Produs Nou
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Nume *
              </label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="Nume produs"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                Cod Produs
              </label>
              <Input
                type="number"
                value={formData.cod || ""}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, cod: e.target.value }))
                }
                placeholder="Cod unic produs"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                AWB Code
              </label>
              <Input
                value={formData.awbCode}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, awbCode: e.target.value }))
                }
                placeholder="Ex: numele prescurtat al cartii"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">ISBN</label>
              <Input
                value={formData.isbn}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, isbn: e.target.value }))
                }
                placeholder="Ex: 978-973-0-00000-0"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium text-gray-700">
                Descriere *
              </label>
              <textarea
                value={formData.description}
                className="min-h-[120px] max-h-[300px] resize-y w-full border-2 border-gray-300 rounded-md p-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                onChange={(e) =>
                  setFormData((f) => ({
                    ...f,
                    description: e.target.value,
                  }))
                }
                placeholder="Descriere produs&#10;&#10;Folosește Enter pentru o nouă linie&#10;Exemplu:&#10;- Carte de spiritualitate&#10;- Autor: Nume Autor&#10;- Anul publicării: 2024"
                required
                rows={5}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                Preț *
              </label>
              <Input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, price: e.target.value }))
                }
                placeholder="Preț produs"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                Stoc *
              </label>
              <Input
                type="number"
                min="0"
                value={formData.stock}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, stock: e.target.value }))
                }
                placeholder="Stoc disponibil"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                Numărul paginilor
              </label>
              <Input
                type="number"
                min="1"
                value={formData.pages}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, pages: e.target.value }))
                }
                placeholder="Numărul de pagini"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                Categorie
              </label>
              <Select
                value={formData.categoryId}
                onValueChange={(value) =>
                  setFormData((f) => ({ ...f, categoryId: value }))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selectează o categorie" />
                </SelectTrigger>
                <SelectContent>
                  {categoriesLoading ? (
                    <div className="flex items-center justify-center p-2">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span>Se încarcă...</span>
                    </div>
                  ) : categories && categories.length > 0 ? (
                    categories.map((category) => (
                      <SelectItem
                        key={category.id}
                        value={category.id.toString()}
                      >
                        {category.name}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="flex items-center justify-center p-2 text-gray-500">
                      Nu există categorii
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Visibility Toggle */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.hide}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, hide: e.target.checked }))
                }
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <div className="flex items-center gap-2">
                {formData.hide ? (
                  <EyeOff className="w-4 h-4 text-gray-500" />
                ) : (
                  <Eye className="w-4 h-4 text-green-600" />
                )}
                <span className="text-sm font-medium text-gray-700">
                  {formData.hide ? "Produs ascuns" : "Produs vizibil"}
                </span>
              </div>
            </label>
            <span className="text-xs text-gray-500">
              Produsele ascunse nu vor fi vizibile pe site-ul public
            </span>
          </div>

          {/* Cover Image Upload */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              Imagine Principală
            </label>

            {formData.imageUrl && !showCoverDropzone ? (
              <div className="flex flex-col items-center">
                <div className="relative rounded-md overflow-hidden w-full max-w-md h-64 bg-gray-100">
                  <img
                    src={formData.imageUrl}
                    alt="Cover"
                    className="w-full h-full object-contain"
                  />
                  <Button
                    className="absolute top-2 right-2 bg-white text-gray-700 hover:bg-gray-100 border shadow"
                    size="sm"
                    onClick={() => setShowCoverDropzone(true)}
                  >
                    Schimbă
                  </Button>
                </div>
              </div>
            ) : (
              <Dropzone {...coverUpload}>
                <DropzoneEmptyState />
                <DropzoneContent />
              </Dropzone>
            )}

            {/* Legacy URL input option */}
            {!formData.imageUrl && (
              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-2">
                  Sau introdu manual URL-ul imaginii:
                </p>
                <Input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, imageUrl: e.target.value }))
                  }
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            )}
          </div>

          {/* Gallery Images Upload */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              Imagini Suplimentare
            </label>

            {/* Display existing gallery images */}
            {formData.images.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {formData.images.map((url, index) => (
                  <div
                    key={index}
                    className="relative group rounded-md overflow-hidden bg-gray-50 border"
                  >
                    <img
                      src={url}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-40 object-cover"
                    />
                    <button
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition bg-white rounded p-1 border shadow"
                      onClick={() => {
                        const newImages = formData.images.filter(
                          (_, i) => i !== index
                        );
                        setFormData((f) => ({ ...f, images: newImages }));
                      }}
                      title="Șterge"
                    >
                      <X className="h-4 w-4 text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Gallery dropzone */}
            {showGalleryDropzone ? (
              <Dropzone {...galleryUpload}>
                <DropzoneEmptyState />
                <DropzoneContent />
              </Dropzone>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowGalleryDropzone(true)}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Adaugă Imagini
              </Button>
            )}

            {/* Legacy URL input option */}
            <div className="mt-4">
              <p className="text-sm text-gray-500 mb-2">
                Sau introdu manual URL-ul unei imagini:
              </p>
              <div className="flex gap-2">
                <Input
                  type="url"
                  value={""}
                  onChange={(e) => {
                    if (e.target.value) {
                      setFormData((f) => ({
                        ...f,
                        images: [...f.images, e.target.value],
                      }));
                      e.target.value = "";
                    }
                  }}
                  placeholder="https://example.com/image.jpg"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const input = document.querySelector(
                      'input[type="url"]:not([value])'
                    ) as HTMLInputElement;
                    if (input && input.value) {
                      setFormData((f) => ({
                        ...f,
                        images: [...f.images, input.value],
                      }));
                      input.value = "";
                    }
                  }}
                >
                  Adaugă
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button type="button" variant="outline" onClick={onCancel}>
              Anulează
            </Button>
            <Button
              type="submit"
              disabled={createProduct.isPending}
              className="flex items-center gap-1 bg-green-600 hover:bg-green-700"
            >
              {createProduct.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Se salvează...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Salvează
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
